import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShare,
  faPaste,
  faCopy,
  faCheck,
  faCircleNotch,
  faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "../../../../../i18n/LanguageContext";
import HelpGuideButton from "../../../HelpGuideButton";
import "../css/EmailCheckStart.css";

const FORWARD_ADDRESS = "security@kinprotect.com";

const CopyAddressChip = () => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(FORWARD_ADDRESS);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard may not be available */
    }
  };

  return (
    <button
      type="button"
      className={`ecs-chip${copied ? " copied" : ""}`}
      onClick={handleCopy}
      aria-label={`${t("email.mobile.copy")} ${FORWARD_ADDRESS}`}
    >
      <span className="ecs-chip-address">{FORWARD_ADDRESS}</span>
      <span className="ecs-chip-action" aria-live="polite">
        <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
        {copied ? t("email.mobile.copied") : t("email.mobile.copy")}
      </span>
    </button>
  );
};

/**
 * Entry point for checking an email. Full variant is the first-run screen
 * (no history yet); compact variant sits above existing history.
 *
 * awaitState: "idle" | "waiting" | "timedOut"
 */
const EmailCheckStart = ({
  variant = "full",
  userEmail,
  awaitState = "idle",
  onStartAwaiting,
  onCancelAwaiting,
  onPasteInstead,
  forwardingDisabled = false,
}) => {
  const { t } = useTranslation();

  const sendFromText = userEmail
    ? t("email.start.forward.step3", { email: userEmail })
    : t("email.start.forward.step3Fallback");
  const timeoutText = userEmail
    ? t("email.start.timeout.body", {
        address: FORWARD_ADDRESS,
        email: userEmail,
      })
    : t("email.start.timeout.bodyFallback", { address: FORWARD_ADDRESS });

  const renderAwaitStatus = (sentLabel) => {
    if (awaitState === "waiting") {
      return (
        <div className="ecs-status ecs-status--waiting" role="status">
          <FontAwesomeIcon icon={faCircleNotch} spin />
          <div>
            <strong>{t("email.start.waiting.title")}</strong>
            <span>{t("email.start.waiting.body")}</span>
          </div>
          <button
            type="button"
            className="ecs-link-btn"
            onClick={onCancelAwaiting}
          >
            {t("email.start.waiting.cancel")}
          </button>
        </div>
      );
    }
    if (awaitState === "timedOut") {
      return (
        <div className="ecs-status ecs-status--timeout" role="status">
          <div>
            <strong>{t("email.start.timeout.title")}</strong>
            <span>{timeoutText}</span>
          </div>
          <span className="ecs-status-actions">
            <button
              type="button"
              className="ecs-link-btn"
              onClick={onStartAwaiting}
            >
              {t("email.start.timeout.retry")}
            </button>
            <button
              type="button"
              className="ecs-link-btn"
              onClick={onCancelAwaiting}
            >
              {t("email.start.timeout.dismiss")}
            </button>
          </span>
        </div>
      );
    }
    return (
      <button
        type="button"
        className={
          variant === "full"
            ? "common-button primary ecs-primary-btn"
            : "ecs-link-btn"
        }
        onClick={onStartAwaiting}
      >
        {sentLabel}
      </button>
    );
  };

  if (variant === "compact") {
    return (
      <div className="ecs-compact">
        <div className="ecs-compact-row">
          <FontAwesomeIcon icon={faEnvelope} className="ecs-compact-icon" />
          {!forwardingDisabled && (
            <>
              <span>{t("email.start.compact.forward")}</span>
              <CopyAddressChip />
              {awaitState === "idle" &&
                renderAwaitStatus(t("email.start.compact.sentBtn"))}
              <span className="ecs-compact-or">
                {t("email.start.compact.or")}
              </span>
            </>
          )}
          <button
            type="button"
            className="ecs-link-btn"
            onClick={onPasteInstead}
          >
            {t("email.start.compact.paste")}
          </button>
        </div>
        {!forwardingDisabled && awaitState !== "idle" && renderAwaitStatus()}
      </div>
    );
  }

  return (
    <section className="ecs" aria-label={t("email.page.title")}>
      <div
        className={`ecs-options${forwardingDisabled ? " ecs-options--single" : ""}`}
      >
        {!forwardingDisabled && (
          <div className="ecs-option ecs-option--primary">
            <span className="ecs-badge">{t("email.start.recommended")}</span>
            <h2 className="ecs-option-title">
              <FontAwesomeIcon icon={faShare} />
              {t("email.start.forward.title")}
            </h2>
            <p className="ecs-option-why">{t("email.start.forward.why")}</p>

            <ol className="ecs-steps">
              <li>{t("email.start.forward.step1")}</li>
              <li>
                {t("email.start.forward.step2")}
                <CopyAddressChip />
              </li>
              <li>
                <strong>{sendFromText}</strong>
                <span className="ecs-step-detail">
                  {t("email.start.forward.step3Detail")}
                </span>
              </li>
            </ol>

            <div className="ecs-help">
              <span>{t("email.start.forward.helpLabel")}</span>
              <HelpGuideButton guideId={1} />
            </div>

            <div className="ecs-actions">
              {renderAwaitStatus(t("email.start.forward.sentBtn"))}
            </div>
          </div>
        )}

        <div className="ecs-option">
          <h2 className="ecs-option-title">
            <FontAwesomeIcon icon={faPaste} />
            {t("email.start.paste.title")}
          </h2>
          <p className="ecs-option-why">{t("email.start.paste.why")}</p>
          <div className="ecs-actions">
            <button
              type="button"
              className="common-button ecs-secondary-btn"
              onClick={onPasteInstead}
            >
              {t("email.start.paste.btn")}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EmailCheckStart;
